// The issues list (Console D, views/triage.js #/issues): issues grouped by cause, scoped by the
// bar's app, release, range and feature flag (step 3.6). The views follow issue states (D100,
// APS-299): Unresolved by default, new in the release, regressed, mine, resolved and ignored,
// with counts; the kind and the assignee are segments; every head but the issue sorts on the
// server. Rows are selected with a checkbox or the keyboard (j/k, x, e, Enter) and changed
// together from the bulk actions, which take the filters' place; the sandbox reads only.
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { isApiError } from '../api/client'
import {
  appsQuery,
  fetchIssues,
  type IssueSort,
  type IssuesParams,
  issueCountsQuery,
  membersQuery,
  missingSymbolsQuery,
  useBulkIssueState,
  windowReleasesQuery,
} from '../api/queries'
import type { IssueStateChange, IssueStateFilter, IssueSummary } from '../api/types'
import { DebugOnlyLine } from '../components/DebugOnly'
import {
  Btn,
  copyText,
  EmptyNote,
  Kbd,
  PageHead,
  Seg,
  TabCount,
  TableFoot,
  useToast,
} from '../components/d'
import { MergeIssues } from '../components/IssueMerges'
import {
  ActionMenu,
  IGNORE_PRESETS,
  ignoreLabel,
  membersOf,
  stateErrorMessage,
} from '../components/IssueStates'
import { IssueTable } from '../components/IssueTable'
import { Icon } from '../components/icons'
import { deviceMeta } from '../components/Perf'
import { ErrorState, Loading } from '../components/states'
import { useI18n } from '../i18n'
import { setIssueOrder } from '../lib/issueOrder'
import { ignoreChange, issueTitle, REOPEN, RESOLVE_NEXT } from '../lib/issues'
import { shortcutOff } from '../lib/keys'
import { busiestApp, releasesNewestFirst } from '../lib/releases'
import { sandboxFor } from '../sandbox'
import { useProjectParams } from './params'
import {
  carried,
  deviceParams,
  type IssueSortSearch,
  type IssuesSearch,
  type IssueTab,
  rangeDays,
} from './search'

const KIND_LABEL = {
  crash: 'issues.kind.crash',
  error: 'issues.kind.error',
  anr: 'issues.kind.anr',
  hang: 'issues.kind.hang',
  abnormal_exit: 'issues.kind.abnormal_exit',
} as const

const NONE: ReadonlySet<string> = new Set()

/** The API's sort for a column the URL names. */
const SORT: Record<IssueSortSearch, IssueSort> = {
  status: 'status',
  kind: 'kind',
  sessions: 'sessions',
  users: 'users',
  first_seen: 'first_seen',
  last_seen: 'last_seen',
}

export function IssuesPage() {
  const i18n = useI18n()
  const { t, number } = i18n
  const { orgId, projectId } = useProjectParams()
  const search = useSearch({ strict: false }) as IssuesSearch
  const navigate = useNavigate()
  // The sandbox is read-only (D64): no selection, no changes, no members, no "mine".
  const sandbox = !!sandboxFor(projectId)
  const days = rangeDays(search.range, 'issues')
  const tab: IssueTab | undefined = sandbox && search.tab === 'mine' ? undefined : search.tab
  const state: IssueStateFilter =
    tab === 'resolved' || tab === 'ignored' || tab === 'all' || tab === 'regressed'
      ? tab
      : 'unresolved'
  const now = Math.floor(Date.now() / 60_000) * 60_000
  const releases = useQuery(windowReleasesQuery(projectId, 30, now))
  const apps = useQuery(appsQuery(orgId, projectId))
  const deviceText = deviceMeta(
    i18n,
    apps.data?.find((a) => a.id === search.app),
    deviceParams(search),
  )
  const members = useQuery({ ...membersQuery(orgId), enabled: !sandbox })
  const appId = search.app
  const list = releases.data
    ? releasesNewestFirst(releases.data, appId ?? busiestApp(releases.data))
    : []
  // "New" is the release the bar names, else the app's newest (D6).
  const newRelease = search.release ?? list[0]?.release
  const isNew = tab === 'new'

  // Hangs on Apple platforms, ANRs on Android: the segment names the kind the apps report.
  const scoped = (apps.data ?? []).filter((a) => !appId || a.id === appId)
  const android = scoped.length > 0 && scoped.every((a) => a.platform === 'android')
  const apple = scoped.length > 0 && scoped.every((a) => a.platform === 'ios')
  const kinds = [
    'crash',
    ...(android ? (['anr'] as const) : apple ? (['hang'] as const) : (['hang', 'anr'] as const)),
    'error',
    'abnormal_exit',
  ] as const

  const assignee = tab === 'mine' ? 'me' : sandbox ? undefined : search.assignee
  const params: IssuesParams = {
    app_id: appId,
    // The API takes one of the two: "new in" already narrows to that release.
    release: isNew ? undefined : search.release,
    new_in: isNew ? newRelease : undefined,
    kind: search.kind,
    flag: search.flag,
    flag_variant: search.flag ? search.variant : undefined,
    days: isNew ? Math.max(days, 30) : days,
    sort: search.sort ? SORT[search.sort] : 'users',
    order: search.order,
    state,
    assignee,
    daily: true,
    limit: 50,
    ...deviceParams(search),
  }
  const issues = useInfiniteQuery({
    queryKey: ['project', projectId, 'issues', 'pages', params],
    queryFn: ({ pageParam }) => fetchIssues(projectId, params, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: !isNew || !!newRelease,
    retry: (count, error) => !isApiError(error, 409) && count < 2,
  })
  // The views' counts, in the bar's scope; "new" is answered for a release only.
  const counts = useQuery(
    issueCountsQuery(projectId, {
      app_id: appId,
      release: search.release ?? newRelease,
      kind: search.kind,
      days,
      ...deviceParams(search),
    }),
  )
  // Images of the release without symbols (D73), under the table.
  const missing = useQuery({
    ...missingSymbolsQuery(projectId, newRelease ?? '', appId),
    enabled: !!newRelease && !sandbox,
  })
  const shown = issues.data?.pages.flatMap((p) => p.issues) ?? []
  const tooMany = isApiError(issues.error, 409)
  // The issue page pages through the list last shown (‹ ›, j/k).
  // biome-ignore lint/correctness/useExhaustiveDependencies: the ids are what changes
  useEffect(() => {
    if (shown.length)
      setIssueOrder(
        projectId,
        shown.map((i) => i.id),
      )
  }, [projectId, shown.map((i) => i.id).join()])

  // Selection belongs to one list: another view or filter starts with nothing selected.
  const listKey = JSON.stringify(params)
  const [selection, setSelection] = useState({ key: listKey, ids: NONE })
  const chosen = selection.key === listKey ? selection.ids : NONE
  const pickedIssues = shown.filter((i) => chosen.has(i.id))
  const picked = pickedIssues.map((i) => i.id)
  // Merging (D102) asks first: the confirmation opens under the bar, on the issues picked when it
  // opened. The merge refreshes the list, which drops the merged-away issues from `shown`; the
  // form keeps its own copy so it stays until the merge answers and says what it did (APS-352).
  // Picking other issues closes it.
  const [merging, setMerging] = useState<IssueSummary[] | null>(null)
  // biome-ignore lint/correctness/useExhaustiveDependencies: closes when the person's own selection changes
  useEffect(() => setMerging(null), [chosen])
  const [cursor, setCursor] = useState(-1)
  const at = Math.min(cursor, shown.length - 1)
  const [announce, setAnnounce] = useState('')
  const bulk = useBulkIssueState(projectId)
  const team = membersOf(members.data)
  const tableRef = useRef<HTMLDivElement>(null)
  const { toast, show } = useToast()

  const select = (ids: ReadonlySet<string>) => setSelection({ key: listKey, ids })
  const toggle = (id: string) => {
    const next = new Set(chosen)
    if (!next.delete(id)) next.add(id)
    select(next)
  }
  const apply = (ids: string[], change: IssueStateChange, done: (count: number) => string) => {
    if (!ids.length || bulk.isPending) return
    setAnnounce('')
    bulk.mutate(
      { issueIds: ids, change },
      {
        onSuccess: (states) => {
          setAnnounce(done(states.length))
          select(NONE)
        },
      },
    )
  }
  const resolve = (ids: string[]) =>
    apply(ids, RESOLVE_NEXT, (count) => t('issues.state.done.resolved', { count }))

  const set = (patch: Partial<IssuesSearch>) =>
    navigate({ to: '.', search: (prev: Record<string, unknown>) => ({ ...prev, ...patch }) })

  // The list's keys, as in the design: j/k move, x selects, e resolves, Enter opens.
  const onKey = useRef<(e: KeyboardEvent) => void>(() => {})
  onKey.current = (e) => {
    if (shortcutOff(e) || shown.length === 0) return
    const focusRow = (index: number) => {
      setCursor(index)
      tableRef.current?.querySelectorAll<HTMLElement>('tbody tr')[index]?.focus()
    }
    const row = at >= 0 ? shown[at] : undefined
    if (e.key === 'j') {
      e.preventDefault()
      focusRow(Math.min(at + 1, shown.length - 1))
    } else if (e.key === 'k') {
      e.preventDefault()
      focusRow(Math.max(at - 1, 0))
    } else if (e.key === 'x' && row && !sandbox) {
      e.preventDefault()
      toggle(row.id)
    } else if (e.key === 'e' && row && !sandbox && state !== 'resolved') {
      e.preventDefault()
      resolve([row.id])
    } else if (
      e.key === 'Enter' &&
      e.target instanceof HTMLElement &&
      e.target.matches('tr[data-issue]') &&
      e.target.dataset.issue
    ) {
      // Enter on a focused row; on its link or a button, Enter does what it always does.
      e.preventDefault()
      navigate({
        to: '/o/$orgId/p/$projectId/issues/$issueId',
        params: { orgId, projectId, issueId: e.target.dataset.issue },
        search: appId ? { app: appId } : {},
      })
    }
  }
  useEffect(() => {
    const listener = (e: KeyboardEvent) => onKey.current(e)
    document.addEventListener('keydown', listener)
    return () => document.removeEventListener('keydown', listener)
  }, [])

  const c = counts.data
  const current: IssueTab | 'unresolved' = tab ?? 'unresolved'
  const tabButton = (id: IssueTab | 'unresolved', label: string, n?: number, disabled = false) => (
    <button
      key={id}
      type="button"
      role="tab"
      aria-selected={current === id}
      disabled={disabled}
      onClick={() => set({ tab: id === 'unresolved' ? undefined : id, assignee: undefined })}
    >
      {label}
      {n !== undefined ? (
        <>
          {' '}
          <TabCount n={number(n)} />
        </>
      ) : null}
    </button>
  )
  // The command an agent runs for this list (the CLI's `apsio issues`).
  const cli = [
    'apsio issues',
    appId ? `--app ${appId}` : '',
    search.release ? `--release "${search.release}"` : '',
    search.kind ? `--kind ${search.kind}` : '',
    `--since ${days}d`,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <main id="main" className="pg">
      <PageHead
        title={t('nav.issues')}
        meta={
          c ? (
            <span translate="no">
              {newRelease && c.new !== undefined
                ? t('issues.meta.release', {
                    unresolved: c.unresolved,
                    new: c.new,
                    regressed: c.regressed,
                    release: newRelease,
                  })
                : t('issues.meta', { unresolved: c.unresolved, regressed: c.regressed })}
              {deviceText ? ` · ${deviceText}` : ''}
            </span>
          ) : null
        }
        actions={
          <>
            <Link
              className="btn"
              to="/o/$orgId/p/$projectId/flags"
              params={{ orgId, projectId }}
              search={carried(search)}
            >
              <Icon name="flag" />
              {t('issues.byFlag')}
            </Link>
            <Btn
              onClick={async () =>
                show((await copyText(cli)) ? t('issues.copied') : t('issues.copyFailed'))
              }
            >
              <Icon name="agent" />
              {t('issues.copyForAgent')}
            </Btn>
          </>
        }
      />
      <section className="sec" aria-label={t('nav.issues')}>
        <div className="is-bar">
          <div className="tabs" role="tablist" aria-label={t('issues.tabs')}>
            {tabButton('unresolved', t('issues.state.tab.unresolved'), c?.unresolved)}
            {tabButton(
              'new',
              newRelease ? t('issues.tab.new', { release: newRelease }) : t('issues.tab.newNone'),
              c?.new,
              !newRelease,
            )}
            {tabButton('regressed', t('issues.state.regressed'), c?.regressed)}
            {sandbox ? null : tabButton('mine', t('issues.tab.mine'), c?.mine)}
            {tabButton('resolved', t('issues.state.tab.resolved'), c?.resolved)}
            {tabButton('ignored', t('issues.state.tab.ignored'), c?.ignored)}
            {tab === 'all' ? tabButton('all', t('issues.tab.all')) : null}
          </div>
          {!sandbox && picked.length > 0 ? (
            <fieldset className="is-bulk">
              <legend className="visually-hidden">{t('issues.state.bulk.label')}</legend>
              <span>{t('issues.state.selected', { count: picked.length })}</span>
              {state !== 'resolved' ? (
                <Btn size="sm" disabled={bulk.isPending} onClick={() => resolve(picked)}>
                  {t('issues.state.bulk.resolve')}
                </Btn>
              ) : null}
              <ActionMenu
                label={t('issues.state.bulk.assign')}
                disabled={bulk.isPending}
                items={[
                  ...team.list.map((m) => ({
                    key: m.id,
                    label: m.name,
                    verbatim: true,
                    onSelect: () =>
                      apply(picked, { assignee_id: m.id }, (count) =>
                        t('issues.state.done.assigned', { count, name: m.name }),
                      ),
                  })),
                  {
                    key: 'none',
                    label: t('issue.actions.unassign'),
                    onSelect: () =>
                      apply(picked, { assignee_id: null }, (count) =>
                        t('issues.state.done.unassigned', { count }),
                      ),
                  },
                ]}
              />
              {state === 'unresolved' || state === 'regressed' || state === 'all' ? (
                <ActionMenu
                  label={t('issues.state.bulk.ignore')}
                  disabled={bulk.isPending}
                  items={IGNORE_PRESETS.slice(0, 3).map(({ key, preset }) => ({
                    key,
                    label: ignoreLabel(t, preset),
                    onSelect: () =>
                      apply(picked, ignoreChange(preset), (count) =>
                        t('issues.state.done.ignored', { count }),
                      ),
                  }))}
                />
              ) : null}
              {state !== 'unresolved' && state !== 'regressed' ? (
                <Btn
                  size="sm"
                  disabled={bulk.isPending}
                  onClick={() =>
                    apply(picked, REOPEN, (count) => t('issues.state.done.reopened', { count }))
                  }
                >
                  {t('issue.actions.reopen')}
                </Btn>
              ) : null}
              {picked.length >= 2 ? (
                <Btn
                  size="sm"
                  aria-expanded={merging !== null}
                  disabled={bulk.isPending}
                  onClick={() => setMerging(merging ? null : pickedIssues)}
                >
                  {t('issues.merge.open')}
                </Btn>
              ) : null}
            </fieldset>
          ) : (
            <div className="is-segs">
              <span className="is-g">
                <span className="is-l" aria-hidden="true">
                  {t('issues.chip.kind')}
                </span>
                <Seg
                  label={t('issues.chip.kind')}
                  value={search.kind ?? ''}
                  onChange={(v) => set({ kind: v ? (v as IssuesSearch['kind']) : undefined })}
                  options={[
                    { value: '', label: t('issues.chip.allKinds') },
                    ...kinds.map((k) => ({ value: k, label: t(KIND_LABEL[k]) })),
                  ]}
                />
              </span>
              {sandbox || tab === 'mine' ? null : (
                <span className="is-g">
                  <span className="is-l" aria-hidden="true">
                    {t('issues.assignee')}
                  </span>
                  <Seg
                    label={t('issues.assignee')}
                    value={search.assignee ?? ''}
                    onChange={(v) => set({ assignee: v ? (v as 'me' | 'none') : undefined })}
                    options={[
                      { value: '', label: t('issues.assignee.anyone') },
                      { value: 'me', label: t('issues.assignee.me') },
                      { value: 'none', label: t('issues.assignee.none') },
                    ]}
                  />
                </span>
              )}
            </div>
          )}
        </div>
        <p role="status" className="is-status note">
          {announce}
        </p>
        {!sandbox && merging && merging.length >= 2 ? (
          <MergeIssues
            projectId={projectId}
            issues={merging}
            onCancel={() => setMerging(null)}
            onMerged={(result) => {
              const kept = merging.find((i) => i.id === result.id)
              setMerging(null)
              select(NONE)
              setAnnounce(
                t('issues.merge.done', {
                  count: result.merged.length,
                  title: kept ? issueTitle(kept) : result.id,
                }),
              )
            }}
          />
        ) : null}
        {bulk.isError ? (
          <p role="alert" className="notice error">
            {stateErrorMessage(t, bulk.error)}
          </p>
        ) : null}
        {issues.isPending && (!isNew || newRelease) ? (
          <Loading />
        ) : tooMany ? (
          <EmptyNote title={t('issues.state.tooMany')}>
            <Btn size="sm" onClick={() => set({ tab: 'all' })}>
              {t('issues.state.tooMany.all')}
            </Btn>
          </EmptyNote>
        ) : issues.isError ? (
          <ErrorState error={issues.error} retry={() => issues.refetch()} />
        ) : shown.length === 0 ? (
          state === 'resolved' ? (
            <EmptyNote title={t('issues.state.empty.resolved')}>
              {t('issues.state.empty.resolvedBody')}
            </EmptyNote>
          ) : state === 'ignored' ? (
            <EmptyNote title={t('issues.state.empty.ignored')}>{t('issues.empty.body')}</EmptyNote>
          ) : (
            <EmptyNote title={isNew ? t('issues.empty.new') : t('issues.empty')}>
              {t('issues.empty.body')}
              <DebugOnlyLine projectId={projectId} scope={{ app: search.app, days }} empty />
            </EmptyNote>
          )
        ) : (
          <div ref={tableRef}>
            <IssueTable
              caption={t('nav.issues')}
              issues={shown}
              release={newRelease}
              orgId={orgId}
              projectId={projectId}
              app={appId}
              assignees={sandbox ? undefined : team.names}
              cursor={{ index: at, set: setCursor }}
              sorting={{
                sort: search.sort,
                asc: search.order === 'asc',
                // A second click on the sorted column turns its order around.
                set: (sort) =>
                  set(
                    (search.sort ?? 'users') === sort
                      ? { sort, order: search.order === 'asc' ? undefined : 'asc' }
                      : { sort, order: undefined },
                  ),
              }}
              selection={
                sandbox
                  ? undefined
                  : {
                      selected: chosen,
                      toggle,
                      toggleAll: (on) => select(on ? new Set(shown.map((i) => i.id)) : NONE),
                    }
              }
            />
          </div>
        )}
        <TableFoot>
          <span className="hints" translate="no">
            <span>
              <Kbd>j</Kbd>
              <Kbd>k</Kbd> {t('issues.state.key.move')}
            </span>
            {sandbox ? null : (
              <>
                <span>
                  <Kbd>x</Kbd> {t('issues.state.key.select')}
                </span>
                <span>
                  <Kbd>e</Kbd> {t('issues.state.key.resolve')}
                </span>
              </>
            )}
            <span>
              <Kbd>↵</Kbd> {t('issues.state.key.open')}
            </span>
          </span>
          <span>
            {/* Network failures live in Performance › Network (D103), not among the issues. */}
            <Link
              className="ul"
              to="/o/$orgId/p/$projectId/performance/network"
              params={{ orgId, projectId }}
              search={appId ? { app: appId } : {}}
            >
              {t('issues.foot.network')}
            </Link>
            {/* The separator goes with the count: an empty list shows none (Mosaic). */}
            {issues.hasNextPage || shown.length > 0 ? ' · ' : null}
            {issues.hasNextPage ? (
              <Btn
                size="sm"
                variant="ghost"
                disabled={issues.isFetchingNextPage}
                onClick={() => issues.fetchNextPage()}
              >
                {t('issues.more')}
              </Btn>
            ) : null}{' '}
            {shown.length > 0
              ? t('issues.foot.count', { count: shown.length, more: issues.hasNextPage ? 1 : 0 })
              : null}
            {missing.data && missing.data.missing.length > 0 && newRelease ? (
              <>
                {' · '}
                <Link
                  className="ul"
                  to="/o/$orgId/p/$projectId/settings/$section"
                  params={{ orgId, projectId, section: 'symbols' }}
                >
                  {t('issues.foot.symbols', {
                    count: missing.data.missing.length,
                    release: newRelease,
                  })}
                </Link>
              </>
            ) : null}
          </span>
        </TableFoot>
        <p className="note">{t('issues.footer')}</p>
      </section>
      {toast}
    </main>
  )
}
